<template>
    <v-dialog v-model="dialogVisible" max-width="550px">
        <v-card>
            <v-card-title class="success white--text">
                <v-icon left color="white">mdi-bank-transfer</v-icon>
                Platobné údaje
                <v-spacer></v-spacer>
                <v-btn icon color="white" @click="dialogVisible = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-6">
                <v-alert type="info" outlined class="mb-4">
                    Dar sa stane aktívnym až po pripísaní sumy na náš účet.
                    Použite prosím nižšie uvedené platobné údaje.
                </v-alert>

                <v-list>
                    <!-- IBAN -->
                    <v-list-item class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">Príjemca</v-list-item-subtitle>
                            <v-list-item-title class="text-body-1 font-weight-medium">{{ company.name }}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>

                    <v-divider></v-divider>

                    <v-list-item class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">IBAN</v-list-item-subtitle>
                            <v-list-item-title class="text-h6 font-weight-bold primary--text">
                                {{ company.iban }}
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="copyText(company.iban, 'IBAN')">
                                <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>

                    <v-divider></v-divider>

                    <!-- Amount -->
                    <v-list-item v-if="amount" class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">Suma na úhradu</v-list-item-subtitle>
                            <v-list-item-title class="text-h5 success--text font-weight-bold">
                                {{ formatCurrency(amount) }}
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="copyText(String(amount), 'Suma')">
                                <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>

                    <v-divider v-if="amount"></v-divider>

                    <!-- Variable Symbol -->
                    <v-list-item v-if="variableSymbol" class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">Variabilný symbol</v-list-item-subtitle>
                            <v-list-item-title class="text-h6 font-weight-bold warning--text">
                                {{ variableSymbol }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-caption">
                                Toto je vaše používateľské ID – bez neho nevieme platbu priradiť.
                            </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="copyText(String(variableSymbol), 'Variabilný symbol')">
                                <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>

                    <v-divider v-if="variableSymbol"></v-divider>

                    <!-- Note / Message -->
                    <v-list-item class="payment-item px-0">
                        <v-list-item-content>
                            <v-list-item-subtitle class="text-caption mb-1">Poznámka / správa pre
                                príjemcu</v-list-item-subtitle>
                            <v-list-item-title class="text-body-1">{{ company.paymentNote }}</v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn icon small @click="copyText(company.paymentNote, 'Poznámka')">
                                <v-icon small>mdi-content-copy</v-icon>
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>

                <v-alert v-if="copySnackbar.show" :type="copySnackbar.color" dense outlined class="mt-4 mb-0">
                    {{ copySnackbar.message }}
                </v-alert>

                <v-card outlined class="mt-4 pa-4 qr-card">
                    <div class="text-subtitle-2 mb-2">QR platba (Pay by square)</div>
                    <div class="text-caption grey--text text--darken-1 mb-3">
                        Naskenujte QR kód v bankovej aplikácii.
                    </div>

                    <div v-if="qrLoading" class="text-center py-6">
                        <v-progress-circular indeterminate size="30" color="primary"></v-progress-circular>
                    </div>

                    <div v-else-if="qrCodeDataUrl" class="text-center">
                        <img :src="qrCodeDataUrl" alt="Pay by square QR" class="payment-qr" />
                    </div>

                    <v-alert v-else :type="qrError ? 'warning' : 'info'" dense outlined class="mb-0">
                        {{ qrError || 'QR kód sa zobrazí po zadaní sumy.' }}
                    </v-alert>
                </v-card>
            </v-card-text>

            <v-card-actions class="px-6 pb-6">
                <v-spacer></v-spacer>
                <v-btn color="primary" large @click="dialogVisible = false">
                    <v-icon left>mdi-check</v-icon>
                    Rozumiem
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { encode, CurrencyCode, PaymentOptions } from 'bysquare/pay';
import QRCode from 'qrcode';
import { COMPANY } from '@/config/investment';
import { formatCurrency } from '@/utils/investment-math';

export default {
    name: "PaymentDetailsDialog",

    props: {
        value: {
            type: Boolean,
            default: false
        },
        amount: {
            type: [Number, String],
            default: null
        },
        variableSymbol: {
            type: [Number, String],
            default: null
        }
    },

    data() {
        return {
            company: { ...COMPANY },
            copySnackbar: {
                show: false,
                message: '',
                color: 'success'
            },
            qrCodeDataUrl: '',
            qrLoading: false,
            qrError: ''
        };
    },

    computed: {
        normalizedAmount() {
            const parsed = Number(this.amount);
            if (!Number.isFinite(parsed) || parsed <= 0) {
                return null;
            }
            return Number(parsed.toFixed(2));
        },
        normalizedIban() {
            return String(this.company.iban || '').replace(/\s+/g, '').toUpperCase();
        },
        dialogVisible: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            }
        }
    },

    watch: {
        dialogVisible(val) {
            if (val) {
                this.generatePaymentQr();
            }
        },
        amount() {
            if (this.dialogVisible) {
                this.generatePaymentQr();
            }
        },
        variableSymbol() {
            if (this.dialogVisible) {
                this.generatePaymentQr();
            }
        }
    },

    mounted() {
        if (this.dialogVisible) {
            this.generatePaymentQr();
        }
    },

    methods: {
        formatCurrency,

        async generatePaymentQr() {
            this.qrCodeDataUrl = '';
            this.qrError = '';

            if (!this.normalizedAmount) {
                this.qrError = 'Pre QR platbu je potrebné zadať sumu.';
                return;
            }

            if (!this.normalizedIban) {
                this.qrError = 'Pre QR platbu chýba IBAN príjemcu.';
                return;
            }

            const variableSymbol = this.variableSymbol != null && this.variableSymbol !== ''
                ? String(this.variableSymbol)
                : undefined;

            this.qrLoading = true;
            try {
                const bysquarePayload = encode({
                    payments: [
                        {
                            type: PaymentOptions.PaymentOrder,
                            amount: this.normalizedAmount,
                            currencyCode: CurrencyCode.EUR,
                            variableSymbol,
                            beneficiary: {
                                name: this.company.name
                            },
                            bankAccounts: [
                                {
                                    iban: this.normalizedIban
                                }
                            ],
                            paymentNote: this.company.paymentNote || undefined
                        }
                    ]
                });

                this.qrCodeDataUrl = await QRCode.toDataURL(bysquarePayload, {
                    width: 240,
                    margin: 1,
                    errorCorrectionLevel: 'M'
                });
            } catch (error) {
                this.qrError = 'Nepodarilo sa vygenerovať QR kód pre platbu.';
            } finally {
                this.qrLoading = false;
            }
        },

        copyText(text, label) {
            const copy = (str) => {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    return navigator.clipboard.writeText(str);
                }
                const el = document.createElement('textarea');
                el.value = str;
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                return Promise.resolve();
            };

            copy(text).then(() => {
                this.copySnackbar.message = `${label} skopírovaný do schránky`;
                this.copySnackbar.color = 'success';
                this.copySnackbar.show = true;
                setTimeout(() => { this.copySnackbar.show = false; }, 2500);
            }).catch(() => {
                this.copySnackbar.message = 'Kopírovanie zlyhalo';
                this.copySnackbar.color = 'error';
                this.copySnackbar.show = true;
                setTimeout(() => { this.copySnackbar.show = false; }, 2500);
            });
        }
    }
};
</script>

<style scoped>
.payment-item {
    min-height: 56px;
    padding-top: 8px;
    padding-bottom: 8px;
}

.qr-card {
    border-color: rgba(0, 0, 0, 0.12);
}

.payment-qr {
    width: 220px;
    max-width: 100%;
    height: auto;
    display: inline-block;
    border-radius: 8px;
}
</style>
